/**
 * AdminMasterData - Stammdaten-Verwaltung
 * 
 * Tabs für:
 * - Benutzer
 * - Kunden
 * - Maschinen
 * - Programme
 * - Strecken
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import {
  Users,
  Building2,
  WashingMachine,
  ListChecks,
  Route,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

type Tab = 'users' | 'customers' | 'machines' | 'programs' | 'lines';

interface Entity {
  id: string;
  name?: string;
  displayName?: string;
  isActive?: boolean;
  [key: string]: any;
}

export default function AdminMasterDataPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('customers');
  const [data, setData] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  // Create state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<Record<string, any>>({});

  const tabs: { key: Tab; label: string; icon: typeof Users }[] = [
    { key: 'users', label: 'Benutzer', icon: Users },
    { key: 'customers', label: 'Kunden', icon: Building2 },
    { key: 'machines', label: 'Maschinen', icon: WashingMachine },
    { key: 'programs', label: 'Programme', icon: ListChecks },
    { key: 'lines', label: 'Strecken', icon: Route },
  ];

  // Check admin access
  if (!user || !['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900">Kein Zugriff</h1>
          <p className="text-gray-500 mt-2">Admin-Berechtigung erforderlich</p>
        </div>
      </div>
    );
  }

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/admin/${activeTab}`, { credentials: 'include' });
      if (res.ok) {
        setData(await res.json());
      } else {
        setError('Fehler beim Laden');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    setEditingId(null);
    setShowCreate(false);
  }, [activeTab]);

  const handleEdit = (item: Entity) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/${activeTab}/${editingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        setEditingId(null);
        loadData();
      }
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/${activeTab}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        setShowCreate(false);
        setCreateForm({});
        loadData();
      }
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Wirklich deaktivieren?')) return;
    try {
      await fetch(`/api/admin/${activeTab}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      loadData();
    } catch {
      // ignore
    }
  };

  const getDisplayName = (item: Entity): string => {
    return item.displayName || item.name || item.number || item.id;
  };

  const renderFields = () => {
    switch (activeTab) {
      case 'users':
        return ['displayName', 'email', 'role', 'isActive'];
      case 'customers':
        return ['name', 'shortName', 'type', 'isActive'];
      case 'machines':
        return ['name', 'type', 'capacityKg', 'minLoadKg', 'maxLoadKg', 'isActive'];
      case 'programs':
        return ['number', 'name', 'type', 'isActive'];
      case 'lines':
        return ['name', 'isActive'];
      default:
        return [];
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stammdaten</h1>
          <p className="text-sm text-slate-500">Benutzer, Kunden, Maschinen verwalten</p>
        </div>
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {tabs.find((t) => t.key === activeTab)?.label}
          </h2>
          {activeTab !== 'users' && (
            <button
              onClick={() => {
                setShowCreate(true);
                setCreateForm({});
              }}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neu
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {renderFields().map((field) => (
                    <th
                      key={field}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      {field}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((item) => (
                  <tr key={item.id} className={!item.isActive ? 'bg-gray-50 opacity-50' : ''}>
                    {renderFields().map((field) => (
                      <td key={field} className="px-4 py-3 text-sm text-gray-900">
                        {editingId === item.id ? (
                          field === 'isActive' ? (
                            <input
                              type="checkbox"
                              checked={editForm[field] ?? true}
                              onChange={(e) =>
                                setEditForm({ ...editForm, [field]: e.target.checked })
                              }
                            />
                          ) : (
                            <input
                              type={typeof item[field] === 'number' ? 'number' : 'text'}
                              value={editForm[field] ?? ''}
                              onChange={(e) =>
                                setEditForm({ ...editForm, [field]: e.target.value })
                              }
                              className="input text-sm w-full"
                            />
                          )
                        ) : field === 'isActive' ? (
                          item[field] ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )
                        ) : (
                          String(item[field] ?? '-')
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      {editingId === item.id ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {activeTab !== 'users' && (
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Neu erstellen
              </h3>
              <div className="space-y-4">
                {renderFields()
                  .filter((f) => f !== 'isActive')
                  .map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field}
                      </label>
                      <input
                        type="text"
                        value={createForm[field] ?? ''}
                        onChange={(e) =>
                          setCreateForm({ ...createForm, [field]: e.target.value })
                        }
                        className="input w-full"
                      />
                    </div>
                  ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreate}
                  disabled={saving}
                  className="flex-1 btn btn-primary"
                >
                  {saving ? 'Speichern...' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
